import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, Bot, Clock, FileText, Folder, HardDrive, Sparkles } from "lucide-react";
import { useMe } from "@/context/MeContext";
import { useFiles } from "@/context/FilesContext";
import { ACTION_LABELS, api, CATEGORY_LABELS, fmtBytes, fmtDate } from "@/lib/api";
import { FileIcon } from "@/components/files/FileIcon";
import { CompanySuggestionsCard } from "@/components/files/CompanySuggestionsCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const Stat = ({ icon: I, label, value, accent, testId }) => (
  <div className="bg-white border rounded-xl p-5" data-testid={testId}>
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</span>
      <I className={`h-4 w-4 ${accent}`} />
    </div>
    <div className="font-display text-3xl font-extrabold tracking-tight mt-3">{value}</div>
  </div>
);

const Panel = ({ title, children, action }) => (
  <section className="bg-white border rounded-xl">
    <div className="flex items-center justify-between px-5 py-4 border-b"><h2 className="font-display font-bold text-base">{title}</h2>{action}</div>
    <div className="p-2">{children}</div>
  </section>
);

export default function Dashboard() {
  const { me } = useMe();
  const { openFile, refreshKey } = useFiles();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [ask, setAsk] = useState("");
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, [refreshKey]);
  const hour = new Date().getHours();

  return (
    <div className="max-w-7xl mx-auto space-y-6 fade-up" data-testid="dashboard-page">
      <div>
        <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">{new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1" data-testid="dashboard-greeting">
          {hour < 18 ? "Bonjour" : "Bonsoir"}, {me.name?.split(" ")[0]}
        </h1>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); ask.trim() && navigate(`/assistant?q=${encodeURIComponent(ask)}`); }}
        className="tread-bg rounded-2xl p-5 sm:p-7 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-center gap-3 md:w-72 shrink-0">
          <span className="h-10 w-10 rounded-lg bg-amber-500/15 flex items-center justify-center"><Sparkles className="h-5 w-5 text-amber-400" /></span>
          <div><div className="text-white font-semibold">Assistant IA</div><div className="text-xs text-slate-400">Interrogez vos documents autorisés</div></div>
        </div>
        <div className="flex-1 flex gap-2">
          <Input value={ask} onChange={(e) => setAsk(e.target.value)} placeholder="Ex. Retrouve toutes les factures Michelin de 2026"
            className="h-11 bg-white/95 border-0" data-testid="dashboard-ai-input" />
          <Button type="submit" className="h-11 bg-amber-600 hover:bg-amber-700" data-testid="dashboard-ai-submit"><Bot className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Demander</span></Button>
        </div>
      </form>

      {d && <>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={FileText} label="Documents" value={d.total_files} accent="text-[#1E3A8A]" testId="stat-files" />
          <Stat icon={Folder} label="Dossiers" value={d.total_folders} accent="text-[#1E3A8A]" testId="stat-folders" />
          <Stat icon={HardDrive} label="Espace utilisé" value={fmtBytes(d.storage_bytes)} accent="text-emerald-600" testId="stat-storage" />
          <Stat icon={AlertTriangle} label="Échéances < 30 j" value={d.expiring.length} accent="text-amber-600" testId="stat-expiring" />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Panel title="Documents récents" action={<Button variant="ghost" size="sm" onClick={() => navigate("/fichiers")} data-testid="dashboard-all-files">Tout voir <ArrowRight className="h-4 w-4 ml-1" /></Button>}>
              {d.recent_files.length === 0 && <p className="text-sm text-slate-500 p-4">Aucun document. Commencez par téléverser vos fichiers dans « Mes fichiers ».</p>}
              {d.recent_files.map((f) => (
                <button key={f.id} onClick={() => openFile(f.id)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-left" data-testid={`recent-file-${f.id}`}>
                  <FileIcon file={f} />
                  <div className="min-w-0 flex-1"><div className="text-sm font-medium truncate">{f.name}</div>
                    <div className="text-xs text-slate-500">{CATEGORY_LABELS[f.category] || "—"} · {fmtDate(f.updated_at, true)}</div></div>
                  <span className="text-xs font-mono text-slate-500">{fmtBytes(f.size)}</span>
                </button>
              ))}
            </Panel>
            <Panel title="Activité récente">
              {d.activity.length === 0 && <p className="text-sm text-slate-500 p-4">Aucune activité.</p>}
              {d.activity.map((a) => (
                <div key={a.id} className="flex items-start gap-3 px-3 py-2 text-sm">
                  <Clock className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                  <div className="min-w-0"><span className="font-medium">{a.user_name}</span> · {ACTION_LABELS[a.action] || a.action} <span className="text-slate-600">{a.target_name}</span>
                    <div className="text-xs text-slate-500">{fmtDate(a.at, true)}</div></div>
                </div>
              ))}
            </Panel>
          </div>
          <div className="space-y-6">
            <CompanySuggestionsCard />
            <Panel title="Échéances et expirations">
              {d.expiring.length === 0 && <p className="text-sm text-slate-500 p-4">Aucun document n'arrive à échéance dans les 30 jours.</p>}
              {d.expiring.map((f) => (
                <button key={f.id} onClick={() => openFile(f.id)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-left" data-testid={`expiring-file-${f.id}`}>
                  <AlertTriangle className={`h-4 w-4 shrink-0 ${f.expired ? "text-red-600" : "text-amber-600"}`} />
                  <div className="min-w-0"><div className="text-sm font-medium truncate">{f.name}</div>
                    <div className={`text-xs ${f.expired ? "text-red-600" : "text-amber-700"}`}>{f.expired ? "Expiré le" : "Échéance le"} {fmtDate(f.expiry)}</div></div>
                </button>
              ))}
            </Panel>
            <Panel title="Accès rapide">
              {d.quick_folders.length === 0 && <p className="text-sm text-slate-500 p-4">Aucun dossier.</p>}
              {d.quick_folders.map((f) => (
                <button key={f.id} onClick={() => navigate(`/fichiers/${f.id}`)} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 text-left" data-testid={`quick-folder-${f.id}`}>
                  <FileIcon folder size="sm" /><span className="text-sm font-medium truncate">{f.name}</span>
                </button>
              ))}
            </Panel>
            <Panel title="Par catégorie">
              <div className="p-3 space-y-2">
                {Object.entries(d.by_category).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                  <div key={k}>
                    <div className="flex justify-between text-xs mb-1"><span>{CATEGORY_LABELS[k] || k}</span><span className="font-mono">{v}</span></div>
                    <div className="h-1.5 bg-slate-100 rounded-full"><div className="h-1.5 bg-[#1E3A8A] rounded-full" style={{ width: `${(v / Math.max(1, d.total_files)) * 100}%` }} /></div>
                  </div>
                ))}
                {d.pending_ai > 0 && <p className="text-xs text-slate-500">{d.pending_ai} document(s) en cours d'analyse IA</p>}
              </div>
            </Panel>
          </div>
        </div>
      </>}
    </div>
  );
}
